import React, { useMemo, useEffect, useState } from "react";
import { isEqual, uniqWith } from "lodash";
import { helpers } from "../../../../common/lib";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useSharedAsset } from "../../../../common/hooks";

/**
 * Use Asset-Data Rules Hook
 * Combines Auditor + Controller + Shared asset data
 * (Now merges auditImages + auditDocuments)
 */
export default (onValueChange) => {
    const { auditAssetId } = useParams();
    const [currentAssetAuditor, setCurrentAssetAuditor] = useState([]);
    const {
        accounts,
        assetAuditors,
        auditControllers,
        auditReview,
        typesList,
        validatedVisibility
    } = useSelector((state) => state);
    const isSharedAsset = useSharedAsset();

    const validatedVisibilityAsset = useMemo(() => {
        return (
            isSharedAsset &&
            validatedVisibility.data.find(
                (item) =>
                    item.auditAsset === isSharedAsset.sharedValidationAsset
            )
        );
    }, [validatedVisibility, isSharedAsset]);

    /** Get Current audit asset and asset controller */
    const getCurrentAuditAsset = (auditAssets) =>
        auditAssets.data.filter(
            (item) => item.auditAsset === parseInt(auditAssetId)
        );

    /** Get Exclusion reason */
    const getExclusionReason = (exclusionReason) => {
        const { exclusionReasonTypes } = typesList;
        if (exclusionReason) {
            return helpers.getListItemForId(
                exclusionReasonTypes,
                exclusionReason
            );
        }
    };

    /** Combine auditor and controller images and documents */
    const combineAuditorControllerData = (next) => {
        const consolidatedArray = next.reduce((prevValue, item) => {
            const isShared = item.auditAsset !== parseInt(auditAssetId);

            /** Merge images */
            if (item.auditImages && item.auditImages.length > 0) {
                item.auditImages.forEach((auditImage) => {
                    auditImage.isSharedAsset = isShared;
                    if (item.auditServices && item.auditServices.length > 0) {
                        const services = item.auditServices.filter(
                            (service) => service.auditImageId === auditImage.id
                        );
                        auditImage.services = services;
                    }
                });
                (prevValue.auditImages ||= []).push(...item.auditImages);
            }

            /** Merge documents */
            if (item.auditDocuments && item.auditDocuments.length > 0) {
                item.auditDocuments.forEach((auditDocument) => {
                    auditDocument.isSharedAsset = isShared;
                });
                (prevValue.auditDocuments ||= []).push(...item.auditDocuments);
            }

            return prevValue;
        }, {});

        /** Remove duplicates */
        const uniqueImages = uniqWith(
            consolidatedArray.auditImages || [],
            isEqual
        );
        const uniqueDocuments = uniqWith(
            consolidatedArray.auditDocuments || [],
            isEqual
        );

        return {
            auditImages: [...uniqueImages, ...uniqueDocuments],
            auditDocuments: uniqueDocuments
        };
    };

    /** Prepare combined data */
    const data = useMemo(() => {
        if (isSharedAsset && validatedVisibilityAsset) {
            const currentAssetAuditor = getCurrentAuditAsset(assetAuditors);
            const curretAuditController =
                getCurrentAuditAsset(auditControllers);

            const next = currentAssetAuditor.concat(
                curretAuditController,
                validatedVisibilityAsset
            );

            return next.map((item) => {
                const auditor = helpers.getListItemForId(
                    accounts,
                    item.auditor
                );
                const exclusionReason =
                    item.exclusionReason &&
                    getExclusionReason(item.exclusionReason);
                return {
                    ...item,
                    auditor,
                    exclusionReason,
                    isPending: true,
                    auditorId: item.auditor,
                    controllerId: item.controller
                };
            });
        } else if (auditReview.auditAssets.seenPending != null) {
            const currentAssetAuditor = getCurrentAuditAsset(assetAuditors);
            const curretAuditController =
                getCurrentAuditAsset(auditControllers);
            const next = currentAssetAuditor.concat(curretAuditController);
            if (next.length > 0)
                return next.map((item) => {
                    const auditor = helpers.getListItemForId(
                        accounts,
                        item.auditor
                    );
                    const exclusionReason =
                        item.exclusionReason &&
                        getExclusionReason(item.exclusionReason);
                    return {
                        ...item,
                        auditor,
                        exclusionReason,
                        isPending: true,
                        auditorId: item.auditor,
                        controllerId: item.controller
                    };
                });
        } else if (auditControllers.data.length > 0) {
            const next =
                getCurrentAuditAsset(assetAuditors) &&
                getCurrentAuditAsset(auditControllers);

            return next.map((item) => {
                const auditor = helpers.getListItemForId(
                    accounts,
                    item.auditor
                );
                const controller = helpers.getListItemForId(
                    accounts,
                    item.controller
                );
                const exclusionReason =
                    item.exclusionReason &&
                    getExclusionReason(item.exclusionReason);
                return {
                    ...item,
                    auditor,
                    controller,
                    exclusionReason,
                    auditorId: item.auditor,
                    controllerId: item.controller
                };
            });
        } else {
            const next = getCurrentAuditAsset(assetAuditors);
            return (
                next &&
                next.map((item) => {
                    const auditor = helpers.getListItemForId(
                        accounts,
                        item.auditor
                    );
                    const exclusionReason =
                        item.exclusionReason &&
                        getExclusionReason(item.exclusionReason);
                    return {
                        ...item,
                        auditor,
                        exclusionReason,
                        auditorId: item.auditor,
                        controllerId: item.controller
                    };
                })
            );
        }
    }, [
        accounts,
        validatedVisibility,
        assetAuditors,
        auditControllers.data,
        auditReview,
        auditAssetId,
        isSharedAsset,
        validatedVisibilityAsset,
        typesList
    ]);

    /** Update on data change */
    useEffect(() => {
        if (data && data.length && onValueChange) {
            const { auditImages, auditDocuments } =
                combineAuditorControllerData(data);

            const next = {
                ...data[0],
                auditImages,
                auditDocuments
            };

            setCurrentAssetAuditor(next);
            onValueChange(next);
        }
    }, [data, validatedVisibilityAsset]);

    return {
        isSharedAsset,
        data,
        currentAssetAuditor
    };
};
